import { VStack, Stack, MenuButton, Image } from '@chakra-ui/react';
import { Button, Link, ButtonLink, FormField, Input, Infobox } from 'Atoms';
import { Typography } from 'Tokens';
import React, { useContext } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { getLanguageMap, useTranslation } from 'utils/translation';
import { useNavigate } from 'react-router-dom';
import { useChangeLanguage } from 'containers/Navigation';
import { useToast } from 'utils/hooks';
import { isServiceAccount } from 'utils/users';
import { Menu } from 'Molecules/Menu';
import { ChevronDownIcon } from 'Tokens/Icons/Direction';
import { LockedIcon } from 'Tokens/Icons/Function';
import { appContext } from 'App';
import { useNhostClient } from 'Providers/nhost/NhostProvider.tsx';

const KNOWN_ERROR = 'already-signed-in';
const INVALID_EMAIL_PASSWORD = 'invalid-email-password';
const INVALID_REQUEST = 'invalid-request';
const SERVICE_ACCOUNT = 'service-account';

export const LanguageSwitcher = () => {
  const map = getLanguageMap();
  const { changeLanguage, language } = useChangeLanguage();
  const languages = Object.entries(map).map(([code, name]) => ({
    id: name,
    onClick: () => changeLanguage(code),
    title: name,
  }));
  return (
    <Menu
      size="lg"
      menuButton={
        <MenuButton as={Button} variant="link" rightIcon={<ChevronDownIcon color="inherit" />}>
          {map[language]}
        </MenuButton>
      }
      sections={[
        {
          actions: languages,
        },
      ]}
    />
  );
};

const LoginErrorBox = ({ error }: { error: string }) => {
  const { t } = useTranslation('login');
  if (error === INVALID_EMAIL_PASSWORD)
    return (
      <Infobox
        closable={false}
        w={'100%'}
        my={'8px'}
        status="warning"
        description={
          <>
            {t('login:incorrectEmailOrPassword')}
            <Link color={'text.action'} to="/changePassword">
              {' '}
              {t('login:resetPassword')}
            </Link>
            .
          </>
        }
      />
    );
  if (error === INVALID_REQUEST)
    return (
      <Infobox
        closable={false}
        minHeight="fit-content"
        w="100%"
        my="8px"
        status="warning"
        description={t('login:invalidEmail')}
      />
    );
  if (error === KNOWN_ERROR)
    return (
      <Infobox
        closable={false}
        w="100%"
        my="8px"
        status="warning"
        description={t('login:nhostDown')}
      />
    );
  if (error === SERVICE_ACCOUNT)
    return (
      <Infobox
        closable={false}
        w="100%"
        my="8px"
        status="warning"
        description={t('login:serviceAccount.cannotLogIn')}
      />
    );
  return <></>;
};

type LoginInput = {
  email: string;
  password: string;
};
export const LoginForm = ({
  onSuccess,
  email,
  disableEmailField = false,
}: {
  onSuccess: () => any;
  email?: string;
  disableEmailField?: boolean;
}) => {
  const nhost = useNhostClient();
  const toast = useToast();
  const { t } = useTranslation('login');
  const [errorType, setErrorType] = React.useState<string>();

  const { register, handleSubmit, control, resetField } = useForm<LoginInput>({
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      email: email || '',
    },
  });
  const { errors, isSubmitting } = useFormState({ control });

  const onLogin = async (values: { email: string; password: string }) => {
    const fields = { ...values, email: values.email ? values.email.trim() : '' };
    if (isServiceAccount(fields.email)) {
      setErrorType(SERVICE_ACCOUNT);
      toast({
        text: t('login:serviceAccount.invalidUser'),
        variant: 'danger',
      });
      return;
    }
    await nhost.auth
      .signInEmailPassword({ email: fields.email, password: fields.password })
      .then(() => {
        onSuccess();
        resetField('password', {
          defaultValue: '',
        });
      })
      .catch((error) => {
        if (error.body.error === INVALID_REQUEST) {
          setErrorType(INVALID_REQUEST);
          toast({
            text: 'Invalid email or password',
            variant: 'danger',
          });
        } else if (error.body.error === INVALID_EMAIL_PASSWORD) {
          setErrorType(INVALID_EMAIL_PASSWORD);
          toast({
            text: 'Invalid email or password',
            variant: 'danger',
          });
        } else if (error.body.error === KNOWN_ERROR) {
          setErrorType(KNOWN_ERROR);
          toast({
            text: t('login:nhostDown'),
            variant: 'danger',
          });
        } else {
          toast({
            text: error.message,
            variant: 'danger',
          });
        }
      });
  };

  const validateEmail = (value: string) => {
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,6}$/i;
    return emailRegex.test(value) || t('login:inputs.email.validateError');
  };

  return (
    <form onSubmit={handleSubmit(onLogin)} style={{ width: '100%' }}>
      <VStack spacing="16px">
        <FormField
          id="email"
          label={t('login:fields.email.label')}
          isInvalid={!!errors.email}
          error={errors.email?.message}
        >
          <Input
            {...(disableEmailField
              ? { value: email }
              : register('email', {
                  required: t('login:inputs.email.required'),
                  validate: validateEmail,
                }))}
            placeholder={t('login:fields.email.label')}
            autoComplete="username"
            size="md"
            width="100%"
            isDisabled={disableEmailField}
          />
        </FormField>
        <FormField
          id="password"
          label={t('login:fields.password.label')}
          isInvalid={!!errors.password}
          error={errors.password?.message ?? ''}
        >
          <Input
            {...register('password', { required: t('login:inputs.password') })}
            type="password"
            placeholder={t('login:fields.password.label')}
            autoComplete="current-password"
            size="md"
            width="100%"
          />
        </FormField>
        {errorType && <LoginErrorBox error={errorType} />}
        <Button variant="primary" width="full" mt="24px" type="submit" isLoading={isSubmitting}>
          {t('login:logIn')}
        </Button>
      </VStack>
    </form>
  );
};

export const LoginPage = ({
  children,
  heading,
  header,
}: {
  heading: string;
  header?: React.ReactNode;
  children: React.ReactNode;
}) => {
  const { usedColorMode } = useContext(appContext);

  return (
    <Stack
      w="100%"
      height="100%"
      alignItems="center"
      gap="0px"
      p="10%"
      background={usedColorMode === 'new' ? 'bg.muted' : 'bg.decorative'}
    >
      <VStack
        spacing="32px"
        width="400px"
        bg="white"
        p="32px"
        borderRadius="8px"
        boxShadow="0px 0px 12px 0px rgba(15, 15, 46, 0.04), inset 0px 0px 0px 0px rgba(0, 0, 0, 0.08)"
      >
        <VStack spacing="8px">
          <Image src={`/assets/iss_logo.svg`} width="172px" />
          <Typography variant="detail" textAlign="center">
            Sustainability Reporting (prev. Celsia)
          </Typography>
          {header
            ? header
            : heading
              ? heading.split('\n\n').map((h) => (
                  <Typography variant="h4" mt="8px" key={h}>
                    {h}
                  </Typography>
                ))
              : null}
        </VStack>
        <VStack width="320px" alignItems="stretch">
          {children}
        </VStack>
        {/* <Box position="absolute" top="lg" right="lg">
        <LanguageSwitcher />
      </Box> */}
      </VStack>
    </Stack>
  );
};

export function Login() {
  const navigate = useNavigate();
  const { t } = useTranslation('login');
  const { usedColorMode } = useContext(appContext);

  return (
    <>
      <VStack
        width="100%"
        height="100vh"
        justifyContent="center"
        alignItems="center"
        padding="40px 0px 0px 0px"
        gap="0px"
        background={usedColorMode === 'new' ? 'bg.muted' : 'bg.decorative'}
      >
        <LoginPage heading="">
          <LoginForm onSuccess={() => navigate('/')} />
          <ButtonLink to="/sso-login" variant="secondary" width="100%">
            {t('login:sso')}
          </ButtonLink>
          <ButtonLink to="/changePassword" variant="ghost" width="100%" leftIcon={<LockedIcon />}>
            {t('login:forgotPassword')}
          </ButtonLink>
        </LoginPage>
      </VStack>
    </>
  );
}
